import { useEffect, useState, useRef } from 'react'
import { PageWrapper, PageFooter, CursorPointer, Flex } from '../../styling/GlobalStyles'
import { useNavigate } from 'react-router-dom'
import { Pagination } from '../../components/Pagination/Pagination'
import Avatar from '../../assets/images/profileAvatar.png'
import { Rating } from './Rating'
import { NavBar } from '../../components/NavBar/NavBar'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearEmployeesList,
  deleteUser,
  getUsersSimpleInfo,
  getUserStats,
} from 'features/users(Employees)/usersSlice'
import { AppDispatch, RootState } from 'store'
import { Options } from 'components/Options/Options'
import { handlePagination, getParams } from 'utils/helpers'
import { setConfirmModal } from 'features/global/globalSlice'
import useAuth from 'hooks/useAuth'
import { ModalTypes } from 'types/global.interface'
import { Modal } from 'components/Modal/Modal'
import { CloseIcon } from 'assets/icons/CloseIcon'
import {
  ImageModalFrame,
  EmployeesHeader,
  EmployeesTitle,
  HeaderActions,
  KpiBand,
  NameCell,
  NameTitle,
  NameRole,
  MutedCell,
  AvatarThumb,
} from './Employees.styled'
import { FilterEnum, LinkEnum } from 'components/Filter/SingleFilter'
import useQueryPagination from 'hooks/useQueryPagination'
import { contractTypes } from './employeesData'
import { ImportEmployeesModal } from './ImportEmployeesModal'
import { attendanceTypesOptions } from 'pages/Attendance/data'
import { TerminateContractModal } from './TerminateContractModal'
import { clearWorkerAvailability, getWorkerAvailability } from 'features/globalView/globalViewSlice'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'

const getInitials = (name?: string) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase()
}

const attendanceStatus = (value?: number) => {
  if (value === 1) return 'success'
  if (value === 2 || value === 6) return 'danger'
  if (value === 3 || value === 9) return 'warning'
  return 'info'
}

export const Employees = () => {
  const { users, totalPages } = useSelector((store: RootState) => store.users)
  const { workersAvailability } = useSelector((state: RootState) => state.globalView)
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [params, setParams] = useState<any>(getParams({ location }))
  const [imageModal, setImageModal] = useState({ isOpen: false, image: null })
  const [importModal, setImportModal] = useState(false)
  const [terminateContractModal, setTerminateContractModal] = useState({ open: false, id: null })

  useQueryPagination({
    fetchFunctions: [getUsersSimpleInfo, getUserStats, getWorkerAvailability],
    cleanUpFunctions: [clearEmployeesList, clearWorkerAvailability],
    params,
  })

  const filters = [
    {
      type: FilterEnum.FIXED_LIST,
      name: 'State',
      state: null,
      propName: 'state',
      sd: true,
      single: true,
      filterOptions: [
        { name: 'Active', id: '1' },
        { name: 'Contract Terminated', id: '3' },
      ],
    },
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Employee Type',
      state: null,
      propName: 'filterType',
      sd: true,
      single: true,
      filterOptions: [
        { name: 'Company', id: '1' },
        { name: 'Contractors', id: '2' },
      ],
    },
    {
      type: FilterEnum.SEARCH,
      link: LinkEnum.ROLES,
      name: 'Roles',
      state: null,
    },
  ]

  const handleDelete = (id: number | string) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(deleteUser(id))
          dispatch(getUserStats())
        },
        setParams: {
          type: ModalTypes.Delete,
          f: setParams,
          noItems: users.length < 2,
        },
        word: 'delete employee',
      }),
    )
  }

  const columns: IHonestTableColumn<any>[] = [
    {
      id: 'id',
      header: 'ID',
      minWidth: '4rem',
      cell: (item) => <MutedCell>{item.accessCardId || '/'}</MutedCell>,
    },
    {
      id: 'avatar',
      header: '',
      minWidth: '3rem',
      cell: (item) => (
        <AvatarThumb
          onClick={(e) => {
            e.stopPropagation()
            setImageModal({ isOpen: true, image: item.getImagePresignedUrl })
          }}
        >
          {item.getImagePresignedUrl ? (
            <img
              src={item.getImagePresignedUrl}
              alt=''
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = Avatar
              }}
            />
          ) : (
            getInitials(item.fullName)
          )}
        </AvatarThumb>
      ),
    },
    {
      id: 'name',
      header: 'Full name',
      minWidth: '12rem',
      fill: true,
      cell: (item) => (
        <NameCell>
          <NameTitle>{item.fullName}</NameTitle>
          {item.role && <NameRole>{item.role}</NameRole>}
        </NameCell>
      ),
    },
    {
      id: 'phone',
      header: 'Phone',
      minWidth: '11rem',
      cell: (item) => (
        <NameCell>
          <MutedCell>{item.phoneNumber || '/'}</MutedCell>
          {item.secondaryPhoneNumber && <NameRole>{item.secondaryPhoneNumber}</NameRole>}
        </NameCell>
      ),
    },
    {
      id: 'salary',
      header: 'Net salary',
      minWidth: '10rem',
      cell: (item) => (
        <MutedCell>
          {item.contract?.value
            ? 'GH₵ ' +
              item.contract.value +
              ' ' +
              (contractTypes.find((c) => c.value == item.contract?.type)?.label || '')
            : 'No active contracts'}
        </MutedCell>
      ),
    },
    {
      id: 'accommodation',
      header: 'Accommodation',
      minWidth: '10rem',
      cell: (item) => <MutedCell>{item.accommodation?.name || 'No accommodation'}</MutedCell>,
    },
    {
      id: 'rating',
      header: 'Rating',
      minWidth: '7rem',
      cell: (item) => <Rating rating={item.overallRating?.toFixed(1)} />,
    },
    {
      id: 'status',
      header: 'Status',
      minWidth: '8rem',
      cell: (item) => {
        const attendanceType = attendanceTypesOptions.find((a) => a.value == item.attendanceType)
        return (
          <StatusPill status={attendanceStatus(attendanceType?.value)}>
            {attendanceType?.label || 'Not set'}
          </StatusPill>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      minWidth: '3rem',
      align: 'right',
      cell: (item) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Options
            item={item}
            invisible={user.id == item.id}
            title='Employee'
            handleRemove={() => handleDelete(item.id)}
            openModal={(id) => setTerminateContractModal({ open: true, id })}
          />
        </div>
      ),
    },
  ]

  const totalWorkers = workersAvailability?.totalWorkers || 0
  const activeWorkers = workersAvailability?.active || 0

  const ImageModal = () => {
    const imageModalRef = useRef(null)

    useEffect(() => {
      const outSideClick = (e) => {
        if (!imageModalRef.current.contains(e.target)) {
          setImageModal({ isOpen: false, image: null })
        }
      }

      document.addEventListener('mousedown', outSideClick)

      return () => {
        document.removeEventListener('mousedown', outSideClick)
      }
    }, [])

    return (
      <Flex column gap='2rem' ref={imageModalRef}>
        <Flex end='true' width='100%'>
          <CursorPointer onClick={() => setImageModal({ isOpen: false, image: null })}>
            <CloseIcon />
          </CursorPointer>
        </Flex>

        <ImageModalFrame>
          <img
            src={imageModal.image || Avatar}
            alt='profile-image'
            onError={({ currentTarget }) => (currentTarget.src = Avatar)}
          />
        </ImageModalFrame>
      </Flex>
    )
  }

  return (
    <>
      <NavBar
        enableTemporaryManualClosing={true}
        filterOptions={filters}
        params={params}
        defaultValue={params.q}
        input
        placeholder='Search Employees...'
        setParams={setParams}
        limit={30}
      />
      <PageWrapper noFooter={totalPages < 2}>
        <EmployeesHeader>
          <EmployeesTitle>Employees</EmployeesTitle>
          <HeaderActions>
            <Button variant='secondary' onClick={() => setImportModal(true)}>
              Import employees
            </Button>
            <Button variant='primary' onClick={() => navigate('/hr/employees/add')}>
              Add employee
            </Button>
          </HeaderActions>
        </EmployeesHeader>

        <KpiBand>
          <MetricCard label='Total workers' value={String(totalWorkers)} />
          <MetricCard label='Active workers' value={String(activeWorkers)} />
          <MetricCard label='Sick leave' value={String(workersAvailability?.sickLeave || 0)} />
          <MetricCard label='Vacation' value={String(workersAvailability?.vacation || 0)} />
        </KpiBand>

        <HonestTable
          columns={columns}
          data={users || []}
          keyField='id'
          columnToggle
          emptyMessage='No employees found'
          onClick={(item) => navigate('/dashboard/employees/' + item.id + '?fromEmployees=true')}
        />

        {imageModal.isOpen && (
          <Modal black>
            <ImageModal />
          </Modal>
        )}
        {importModal && <ImportEmployeesModal closeModal={() => setImportModal(false)} />}
        {terminateContractModal.open && (
          <TerminateContractModal
            id={terminateContractModal.id}
            closeModal={() => setTerminateContractModal({ open: false, id: null })}
          />
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
